import React from 'react';

export default function PrivacyPolicy() {
  return (
    <div className="relative min-h-screen bg-white">
      <button
        onClick={() => window.history.back()}
        className="fixed top-6 left-6 z-50 flex items-center px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 text-gray-700 text-base shadow-sm bg-white"
        aria-label="Return to previous page"
      >
        <span className="mr-2">←</span> Return to previous page
      </button>
      <div className="max-w-4xl mx-auto py-16 px-6">
        <h1 className="text-3xl font-bold mb-6 mt-8">Bloom Scouting Privacy Policy</h1>
        <div className="prose prose-lg text-gray-700">
          <p><strong>Effective Date: 07/03/2025</strong></p>
          
          <h2>1. Introduction</h2>
          <p>Bloom Scouting ("Bloom Scouting," "we," "our," or "us") is a software platform that helps collegiate coaches efficiently manage and respond to athlete‑recruitment e‑mails. Protecting your privacy—and the privacy of your student‑athletes—is fundamental to our mission. This Privacy Policy explains how we collect, use, store, and share information, including Google and Microsoft user data obtained through OAuth restricted scopes, and how you can control that information. A concise in‑product privacy banner ("Bloom Scouting reads only recruitment emails you select. Learn more.") links to this Policy from every page of the application.</p>
          
          <h2>2. Scope of This Policy</h2>
          <p>This Policy applies to information collected through the Bloom Scouting web application, any related mobile application, and our public domains bloomscouting.com and bloom-frontend-c8td.onrender.com (collectively, the "Services"). It governs all users, including coaches, staff, and institutional administrators.</p>
          
          <h2>3. Information We Collect</h2>
          <h3>3.1 Information You Provide Directly</h3>
          <p><strong>Account & Profile Data</strong> – name, institutional e‑mail address, role, password or single‑sign‑on token managed via Supabase Auth (supports email/password, Google OAuth, and Microsoft OAuth), and communication preferences.</p>
          <p><strong>Support Communications</strong> – messages you send us via chat, e‑mail, or feedback forms.</p>
          
          <h3>3.2 Information Obtained From Your E‑mail Provider</h3>
          <p>When you connect your mailbox, you expressly grant Bloom Scouting permission to access e‑mail data via:</p>
          <ul>
            <li><strong>Google Gmail scopes:</strong> gmail.readonly and gmail.send</li>
            <li><strong>Microsoft Outlook scopes:</strong> https://graph.microsoft.com/Mail.Read and https://graph.microsoft.com/Mail.Send</li>
          </ul>
          <p>These scopes allow Bloom Scouting Inc. to read email messages (content and metadata) without altering or deleting them and to send emails on your behalf with your explicit permission. This access facilitates communication tracking, automated outreach, email response management, and improvements to our services.</p>
          
          <p><strong>How we process these e‑mails:</strong></p>
          <ul>
            <li>Upon your command ("Refresh Emails"), our servers temporarily fetch the full text and headers of the most recent 1,000 messages in your inbox.</li>
            <li>We immediately run a classifier to identify recruitment e‑mails.</li>
            <li>Only messages passing this filter are securely sent to OpenAI's ChatGPT API for data extraction (player name, grad year, position, contact info, etc.).</li>
            <li>Parsed data and selected full email bodies identified as recruitment-related are securely stored using AES‑256 encryption, accessible only by the coach who originally received the e‑mail.</li>
            <li>Non‑recruitment e‑mails are immediately discarded and never stored.</li>
          </ul>
          
          <h3>3.3 Automatically Collected Technical Data</h3>
          <p>We automatically collect certain technical information:</p>
          <ul>
            <li>Browser type and settings</li>
            <li>Device identifiers and OS details</li>
            <li>Crash reports</li>
            <li>Usage statistics (page views, feature usage, session duration)</li>
          </ul>
          
          <h2>4. How We Use Information</h2>
          <p>We use collected information to:</p>
          <ul>
            <li>Provide, operate, maintain, and improve Bloom Scouting's core features.</li>
            <li>Facilitate sending emails through Gmail gmail.send or Outlook https://graph.microsoft.com/Mail.Send.</li>
            <li>Secure the Services, detect fraud, and prevent abuse.</li>
            <li>Provide customer support and product enhancements.</li>
            <li>Comply with legal obligations.</li>
          </ul>
          <p><strong>No Advertising</strong> – Bloom Scouting does not use your data for advertising, marketing, or unrelated analytics.</p>
          
          <h2>5. Data Sharing and Disclosure</h2>
          <p>We do not sell, rent, or trade personal information or email content. Data sharing is limited to:</p>
          <ul>
            <li><strong>Service Providers</strong> – Render (hosting), Supabase (database/auth), OpenAI (structured extraction), all bound by confidentiality.</li>
            <li><strong>Legal Requirements</strong> – when compelled by law or court order after user notification unless prohibited.</li>
            <li><strong>Business Transfers</strong> – in the event of a merger/acquisition, with notice.</li>
          </ul>
          
          <h2>6. Data Security</h2>
          <ul>
            <li>Encryption in transit (TLS 1.2+) and at rest (AES‑256)</li>
            <li>Supabase managed PostgreSQL with encryption and backups</li>
            <li>Zero‑trust, least‑privilege access controls</li>
            <li>Continuous monitoring and logging</li>
            <li>Upon reaching 100 Gmail users, we will complete Google's required security assessment</li>
          </ul>
          
          <h2>7. Data Retention and Deletion</h2>
          <p>Structured player data and recruitment email bodies are retained until you delete records, disconnect your mailbox, or close your account. Upon deletion or disconnection, data is purged from storage and backups within 30 days.</p>
          
          <h2>8. Misclassification & User Controls</h2>
          <p>Occasionally, non‑recruitment e‑mails may be flagged as recruiting messages. If you notice this:</p>
          <ul>
            <li>Delete the entry via the Bloom Scouting dashboard, or</li>
            <li>Email lucas@bloomscouting.com or gfmorone@berkeley.edu, and we will remove it within 24 hours.</li>
          </ul>
          
          <h2>9. Compliance with Google API Services User Data Policy</h2>
          <p>Bloom Scouting Inc. complies fully with Google's policy, including Limited Use requirements:</p>
          <ul>
            <li>Data usage restricted to requested email parsing and reply features.</li>
            <li>Human reading of data only with explicit consent, for security/legal purposes, or feature improvements.</li>
            <li>No data shared externally beyond described providers.</li>
          </ul>
          
          <h2>10. Educational Records & FERPA Compliance</h2>
          <p>Bloom Scouting Inc. operates as a "school official" under FERPA (34 CFR §99.31(a)(1)). We will execute FERPA-aligned Data Protection Addenda upon request.</p>
          
          <h2>11. Children's Privacy</h2>
          <p>Bloom Scouting is intended for adult collegiate staff. We do not knowingly collect personal information from children under 13; such data is promptly deleted upon discovery.</p>
          
          <h2>12. International Transfers</h2>
          <p>Bloom Scouting operates in the U.S. International data transfers rely on Standard Contractual Clauses or equivalent safeguards.</p>
          
          <h2>13. Changes to This Privacy Policy</h2>
          <p>Changes will be posted here with updated dates and communicated via e‑mail or in‑app banner for material updates.</p>
          
          <h2>14. Your Privacy Rights (EU/California Residents)</h2>
          <p>Residents of EEA, UK, and California may:</p>
          <ul>
            <li>Access, rectify, or erase data via provided account tools</li>
            <li>Export data in structured JSON format</li>
            <li>Withdraw consent via Settings → Integrations</li>
          </ul>
          <p>To exercise these rights, email gfmorone@berkeley.edu (response within 30 days).</p>
          
          <h2>15. OpenAI Processing</h2>
          <p>Bloom Scouting uses OpenAI's ChatGPT API for structured data extraction:</p>
          <ul>
            <li>The data is not used to train or improve OpenAI models.</li>
            <li>Processing takes place in OpenAI's U.S. data centers.</li>
            <li>OpenAI does not retain the data after processing is complete.</li>
            <li>Bloom Scouting Inc. maintains a Data Processing Agreement (DPA) with OpenAI that ensures confidentiality and compliance with the EU‑U.S. Data Privacy Framework and other applicable safeguards.</li>
          </ul>
          
          <h2>16. Bloom AI Processing</h2>
          <p>To enhance our Services, we may use selected email content to improve our in-house athlete email classification, data extraction, and future email composition assistant tools. These models are proprietary and remain solely within Bloom Scouting Inc.</p>
          
          <h2>17. Security Incident Response</h2>
          <p>In case of a data breach, we will:</p>
          <ul>
            <li>Notify affected users within 72 hours</li>
            <li>Inform authorities as required</li>
            <li>Transparently communicate incident details and remediation steps</li>
          </ul>
          <p>For urgent concerns, contact +1‑510‑714‑0864</p>
          
          <h2>18. Cookies and Tracking</h2>
          <p>Bloom Scouting uses only essential cookies for:</p>
          <ul>
            <li>Authentication sessions</li>
            <li>Security (CSRF protection)</li>
            <li>User preferences (language/theme)</li>
          </ul>
          <p>We do not use advertising/tracking cookies.</p>
          
          <h2>19. Contact Us</h2>
          <p>Bloom Scouting Inc. Privacy Team<br />
          Email: lucas@bloomscouting.com<br />
          Address: 2425 Prospect St, Berkeley, CA 94704</p>
          
          <p>If unresolved concerns persist, contact your local data‑protection authority.</p>
        </div>
      </div>
    </div>
  );
} 